'use client';

import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { PlayIcon } from '@heroicons/react/20/solid';
import { Transition } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import { VideoFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';

// Dynamic import avoids the hydration mismatch react-player causes during SSR.
const ReactPlayer = dynamic(() => import('react-player'));

export interface GalleryVideoProps {
  video: VideoFragment;
  isActive: boolean;
}

export function GalleryVideo({ video, isActive }: Readonly<GalleryVideoProps>) {
  const t = useTranslations('Video');
  const [playing, setPlaying] = useState(false);

  // Stop playback once this slide is no longer the visible one.
  useEffect(() => {
    if (!isActive) {
      setPlaying(false);
    }
  }, [isActive]);

  return (
    <div className="gallery-video relative mx-auto h-full w-full overflow-hidden rounded-lg">
      <ReactPlayer
        url={video.playlists?.[1]}
        width="100%"
        height="100%"
        playing={playing}
        controls={playing}
        playsinline
      />
      {/* No thumbnail poster — the video player itself is the main content. Just overlay a play
          button that fades out on play. */}
      <Transition
        show={!playing}
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={t('playVideo')}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100/70 text-white transition-colors hover:bg-neutral-100"
          >
            <PlayIcon className="h-7 w-7" aria-hidden="true" />
          </button>
        </div>
      </Transition>
    </div>
  );
}
